import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Linkedin, Instagram, Github, Star, ArrowRight } from 'lucide-react';
import { SiDevdotto, SiHackernoon, SiHashnode, SiMedium, SiSubstack, SiIndiehackers, SiX, SiPeerlist } from 'react-icons/si';
import Footer from '../components/Footer';
import Breadcrumb from '../components/Breadcrumb';
import ContactForm from '../components/ContactForm';
import ProfileCard from '../components/ProfileCard';

/* Importers: src/App.js (route /contact)
   Affected API: Default export ContactPage component
   Data schema: Cross-linked product cards as narrative chapters
   User instruction: "Update ContactPage.jsx with cross-linked product cards as chapters" */

const jsonLd = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "Organization",
      "name": "Dopove",
      "url": "https://dopove.com",
      "logo": "https://dopove.com/logo_new_16x9_trans_inverted.png",
      "sameAs": [
        "https://x.com/dopove",
        "https://peerlist.io/dopove",
        "https://github.com/dopove",
        "https://linkedin.com/company/dopove"
      ],
      "foundingDate": "2024",
      "description": "Dopove builds useful memory for AI agents and applies systems thinking to how it works. Its first product is ICE, the Indexless Context Engine.",
      "contactPoint": [
        {
          "@type": "ContactPoint",
          "contactType": "customer service",
          "availableLanguage": "English",
          "areaServed": "WORLD"
        },
        {
          "@type": "ContactPoint",
          "contactType": "sales",
          "availableLanguage": "English",
          "areaServed": "WORLD"
        },
        {
          "@type": "ContactPoint",
          "contactType": "technical support",
          "availableLanguage": "English",
          "areaServed": "WORLD"
        }
      ]
    },
    {
      "@type": "ContactPage",
      "name": "Contact Dopove",
      "description": "Get in touch with Dopove about ICE, GoingMerry or Cortex: sales, support, partnerships, careers and media enquiries.",
      "url": "https://dopove.com/contact",
      "mainEntity": {
        "@type": "Organization",
        "name": "Dopove"
      }
    }
  ]
};

const ContactPage = () => (
  <div className="bg-[#191d2b] min-h-screen text-white">
    <Helmet>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
      <title>Contact Dopove</title>
      <meta name="description" content="Get in touch with Dopove about ICE, GoingMerry or Cortex: sales, support, partnerships, careers and media enquiries." />
      <meta property="og:title" content="Contact Dopove" />
      <meta property="og:description" content="Get in touch with Dopove about ICE, GoingMerry or Cortex: sales, support, partnerships, careers and media enquiries." />
      <meta property="og:image" content="https://dopove.com/logo_new_16x9_trans_inverted.png" />
      <meta property="og:url" content="https://dopove.com/contact" />
      <meta property="og:type" content="website" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content="Contact Dopove" />
      <meta name="twitter:description" content="Get in touch with Dopove about ICE, GoingMerry or Cortex: sales, support, partnerships, careers and media enquiries." />
      <meta name="twitter:image" content="https://dopove.com/logo_new_16x9_trans_inverted.png" />
      <link rel="canonical" href="https://dopove.com/contact" />
    </Helmet>

    
    <main className="pt-20 max-w-5xl mx-auto px-6">
      <Breadcrumb items={[{ label: 'Home', href: '/' }, { label: 'Contact' }]} className="mb-4" />
      {/* Hero Section */}
      <motion.section
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
        className="text-center py-24"
      >
        <div className="flex justify-center items-center relative">
          <div className="absolute w-2/3 h-2/3 bg-white/30 blur-3xl rounded-full"></div>
          <img
            src="/full_logo_16x9.png"
            alt="DOPOVE Logo"
            className="h-auto w-full max-w-lg mb-8 relative"
          />
        </div>
        <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-[#FFFFFF]">Contact Dopove</h1>
        <p className="max-w-3xl mx-auto mt-4 text-[#FFFFFF]/70 text-lg">
          Questions about ICE, GoingMerry or Cortex, sales, support, partnerships, careers or press.
        </p>
      </motion.section>
      {/* About Section */}
      <motion.section
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true, amount: 0.5 }}
        transition={{ duration: 0.8 }}
        className="py-24"
      >
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div>
              <h2 className="text-4xl font-bold mb-6">Who we are</h2>
              <p className="text-[#FFFFFF]/70 text-lg leading-relaxed">
                Dopove is a systems thinking company. We build useful memory for AI agents, and we apply the same thinking to how we run our own marketing.
              </p>
            </div>
            <div
              className="bg-gradient-to-br from-slate-800/80 to-slate-700/40 p-8 rounded-2xl border border-slate-600 hover-lift focus-visible-ring transition-[transform,border-color,background-color,box-shadow] duration-200 ease-[var(--ease-out)]"
            >
              <h3 className="text-3xl font-bold mb-4">Our products</h3>
              <p className="text-[#FFFFFF]/40 text-sm mb-6">Pick the product your question is about.</p>
              <div className="space-y-4">
                <Link
                  to="/ice"
                  className="group block bg-gradient-to-br from-slate-800/60 to-slate-900/40 p-6 rounded-xl border border-slate-700/50 hover:border-[#1E7FD1]/50 transition-[transform,border-color,background-color] duration-200 ease-[var(--ease-out)] hover:translate-y-[-2px]"
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <span className="text-xs font-mono text-[#1E7FD1] tracking-wider">Our first product</span>
                      <h4 className="text-2xl font-bold text-[#6FB6E8] mt-1 group-hover:text-[#1E7FD1] transition-colors">ICE</h4>
                      <p className="text-[#FFFFFF]/60 mt-2">Indexless Context Engine. Turn-granular memory for AI agents.</p>
                    </div>
                    <ArrowRight className="w-5 h-5 text-[#6FB6E8]/40 group-hover:text-[#1E7FD1] transition-colors" />
                  </div>
                </Link>
                <Link
                  to="/goingmerry"
                  className="group block bg-gradient-to-br from-slate-800/60 to-slate-900/40 p-6 rounded-xl border border-slate-700/50 hover:border-[#62B7FF]/50 transition-[transform,border-color,background-color] duration-200 ease-[var(--ease-out)] hover:translate-y-[-2px]"
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <span className="text-xs font-mono text-[#62B7FF] tracking-wider">The next product after ICE</span>
                      <h4 className="text-2xl font-bold text-[#62B7FF] mt-1 group-hover:text-[#3DA3FF] transition-colors">GoingMerry</h4>
                      <p className="text-[#FFFFFF]/60 mt-2">Local AI runtime. Run models privately on your own hardware.</p>
                    </div>
                    <ArrowRight className="w-5 h-5 text-[#62B7FF]/40 group-hover:text-[#3DA3FF] transition-colors" />
                  </div>
                </Link>
                <div className="bg-gradient-to-br from-slate-800/60 to-slate-900/40 p-6 rounded-xl border border-slate-700/50">
                  <div className="flex items-start justify-between">
                    <div>
                      <span className="text-xs font-mono text-[#BFDFF6] tracking-wider">Open source, on GitHub</span>
                      <h4 className="text-2xl font-bold text-[#BFDFF6] mt-1">Cortex</h4>
                      <p className="text-[#FFFFFF]/60 mt-2">Multi-agent orchestration runtime. Auditable, not just functional.</p>
                    </div>
                    <a
                      href="https://github.com/Dopove/Cortex"
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Cortex on GitHub"
                      className="text-[#BFDFF6]/40 hover:text-[#BFDFF6] transition-colors"
                    >
                      <Github className="w-5 h-5" />
                    </a>
                  </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      </motion.section>
      {/* Values Section */}
      <motion.section
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true, amount: 0.5 }}
        transition={{ duration: 0.8 }}
        className="py-24"
      >
        <h2 className="text-4xl md:text-5xl font-bold text-center mb-12">Our Values</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
          {/* Value Cards */}
          {[
            {icon: '💡', title: 'Invention', text: "Pushing the boundaries of the possible."},
            {icon: '⚡', title: 'Precision', text: "Building with absolute performance in mind."},
            {icon: '🌟', title: 'Quality', text: "High-grade engineering in every detail."},
            {icon: '🌍', title: 'Scale', text: "Solutions designed for the global horizon."}
          ].map((v,i) => (
            <div
              key={v.title}
              className="bg-gradient-to-br from-slate-800/80 to-slate-700/40 p-8 rounded-2xl border border-slate-600 text-center hover-lift focus-visible-ring transition-[transform,border-color,background-color,box-shadow] duration-200 ease-[var(--ease-out)]"
            >
              <span className="text-4xl">{v.icon}</span>
              <h3 className="text-2xl font-bold mt-4">{v.title}</h3>
              <p className="mt-2 text-[#FFFFFF]/40">{v.text}</p>
            </div>
          ))}
        </div>
      </motion.section>

      {/* Socials Section */}
      <motion.section
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true, amount: 0.5 }}
        transition={{ duration: 0.8 }}
        className="py-24 border-t border-white/5"
      >
        <div className="max-w-6xl mx-auto px-6 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-12">Connect With Us</h2>
          <div className="flex flex-wrap justify-center gap-6">
            {[
              { name: 'LinkedIn', url: 'https://www.linkedin.com/company/dopove', icon: Linkedin },
              { name: 'X', url: 'https://x.com/dopove_ai', icon: SiX },
              { name: 'Instagram', url: 'https://www.instagram.com/_dopove_', icon: Instagram },
              { name: 'Peerlist', url: 'https://peerlist.io/company/dopove', icon: SiPeerlist },
              { name: 'GitHub', url: 'https://github.com/Dopove', icon: Github },
              { name: 'G2', url: 'https://www.g2.com/sellers/dopove', icon: Star },
              { name: 'Dev.to', url: 'https://dev.to/dopove', icon: SiDevdotto },
              { name: 'Hackernoon', url: 'https://hackernoon.com/u/dopove', icon: SiHackernoon },
              { name: 'Hashnode', url: 'https://hashnode.com/@dopove', icon: SiHashnode },
              { name: 'Medium', url: 'https://dopove.medium.com/', icon: SiMedium },
              { name: 'Substack', url: 'https://dopove.substack.com/', icon: SiSubstack },
              { name: 'IndieHackers', url: 'https://www.indiehackers.com/dopove', icon: SiIndiehackers }
            ].map((social) => {
              const Icon = social.icon;
              return (
                <a
                  key={social.name}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex flex-col items-center justify-center w-28 h-28 rounded-2xl bg-[#23263a]/50 border border-[#FFFFFF]/10 text-[#FFFFFF]/80 hover:text-white hover:bg-[#1E7FD1]/20 hover:border-[#1E7FD1]/50 hover-lift focus-visible-ring transition-[transform,border-color,background-color,box-shadow] duration-200 ease-[var(--ease-out)] shadow-lg"
                >
                  <Icon aria-hidden="true" className="w-8 h-8 mb-3 hover:text-[#6FB6E8] transition-colors" />
                  <span className="text-sm font-medium">{social.name}</span>
                </a>
              );
            })}
          </div>
        </div>
      </motion.section>

      {/* Leadership Section */}
      <motion.section
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true, amount: 0.3 }}
        transition={{ duration: 0.8 }}
        className="py-24"
      >
        <h2 className="text-4xl md:text-5xl font-bold text-center mb-4">Leadership</h2>
        <p className="max-w-2xl mx-auto text-center text-[#FFFFFF]/40 mb-16">
          The mind behind Dopove
        </p>
        <div className="flex justify-center">
          <ProfileCard
            name="Saran"
            title="Founder & CEO"
            handle="saran"
            status="Building the future"
            contactText="Get in Touch"
            avatarUrl="/photo-output.jpeg"
            showUserInfo
            enableTilt
            onContactClick={() => {
              const el = document.getElementById('contact-section');
              if (el) el.scrollIntoView({ behavior: 'smooth' });
            }}
            behindGlowColor="rgba(56, 189, 248, 0.35)"
            innerGradient="linear-gradient(145deg, #1e293b 0%, #0f172a 50%, #1a1a2e 100%)"
          />
        </div>
      </motion.section>

      {/* NVIDIA Inception Section */}
      <motion.section
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true, amount: 0.5 }}
        transition={{ duration: 0.8 }}
        className="py-24 border-y border-white/5 bg-white/5"
      >
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            whileInView={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.5 }}
            className="mb-10 flex justify-center"
          >
            <img 
              src="/nvidia-inception-program-badge-rgb-for-screen.png" 
              alt="NVIDIA Inception Program Member" 
              className="h-24 md:h-32 w-auto object-contain drop-shadow-[0_0_20px_rgba(118,185,0,0.2)]"
            />
          </motion.div>
          <h2 className="text-3xl md:text-4xl font-bold mb-6 text-[#FFFFFF]">Official Program Membership</h2>
          <p className="text-xl text-[#FFFFFF]/70 font-light leading-relaxed max-w-2xl mx-auto mb-10">
            Dopove is a member of the <span className="font-semibold text-[#76B900]">NVIDIA Inception program</span>, which nurtures startups transforming industries with advancements in AI and data sciences.
          </p>
          <p className="text-[10px] text-[#FFFFFF]/30 font-mono tracking-wider max-w-2xl mx-auto uppercase">
            © 2025 NVIDIA, the NVIDIA logo, and NVIDIA Inception are trademarks and/or registered trademarks of NVIDIA Corporation in the U.S. and other countries.
          </p>
        </div>
      </motion.section>

      {/* Contact Form Section */}
      <motion.section
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true, amount: 0.3 }}
        transition={{ duration: 0.8 }}
        className="py-24"
        id="contact-section"
      >
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold text-center mb-4">Get in Touch</h2>
          <p className="max-w-2xl mx-auto text-center text-[#FFFFFF]/40 mb-12">
            We're always looking for passionate individuals. If you are interested or have questions, reach out.
          </p>
          <ContactForm />
        </div>
      </motion.section>
    </main>
    <Footer />
  </div>
);

export default ContactPage;
